import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, DollarSign, ArrowLeft, Tag, User, 
  Calendar, CheckCircle, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  open: 'border-green-500/50 text-green-400 bg-green-500/10',
  in_progress: 'border-yellow-500/50 text-yellow-400 bg-yellow-500/10',
  completed: 'border-blue-500/50 text-blue-400 bg-blue-500/10',
  closed: 'border-zinc-500/50 text-zinc-400 bg-zinc-500/10'
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hiredAgent, setHiredAgent] = useState(null);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${API}/jobs/${id}`);
        setJob(response.data);
        
        // Fetch hired agent if exists
        if (response.data.hired_agent_id) {
          try {
            const agentResponse = await axios.get(`${API}/agents/${response.data.hired_agent_id}`);
            setHiredAgent(agentResponse.data);
          } catch (e) {
            console.error('Error fetching hired agent:', e);
          }
        }
      } catch (error) {
        toast.error('Failed to load job');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="job-detail-page">
      <div className="max-w-4xl mx-auto px-6">
        {/* Back button */}
        <button 
          onClick={() => navigate('/jobs')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </button>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
            <div className="flex flex-wrap items-start gap-4 mb-6">
              <Badge 
                variant="outline" 
                className={`${statusColors[job.status] || statusColors.open} capitalize text-sm px-3 py-1`}
              >
                {job.status?.replace('_', ' ') || 'Open'}
              </Badge>
              <Badge variant="outline" className="border-indigo-500/50 text-indigo-400 bg-indigo-500/10">
                <Tag className="w-3 h-3 mr-1" />
                {job.category}
              </Badge>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{job.title}</h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-400">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Posted by {job.client_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(job.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Budget</p>
                  <p className="text-2xl font-bold text-green-400">${job.budget?.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Duration</p>
                  <p className="text-2xl font-bold text-cyan-400">{job.duration_hours}h</p>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-white mb-4">Job Description</h2>
            <p className="text-zinc-400 leading-relaxed whitespace-pre-wrap">{job.description}</p>
          </div>

          {/* Required Capabilities */}
          <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-white mb-4">Required Capabilities</h2>
            <div className="flex flex-wrap gap-3">
              {job.required_capabilities?.map((cap, idx) => (
                <span 
                  key={idx} 
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-zinc-300 text-sm"
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>

          {/* Hired Agent */}
          {hiredAgent && (
            <div className="bg-zinc-900/50 border border-cyan-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                <h2 className="text-xl font-semibold text-white">Hired Agent</h2>
              </div>
              <div 
                className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/agents/${hiredAgent.id}`)}
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden border border-white/10">
                  <img 
                    src={hiredAgent.avatar_url || 'https://images.unsplash.com/photo-1698793916454-2f656c622087?w=100'} 
                    alt={hiredAgent.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-white font-semibold">{hiredAgent.name}</h3>
                  <p className="text-zinc-400 text-sm">{hiredAgent.category}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {user && user.user_type === 'agent_owner' && job.status === 'open' && (
            <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8 text-center">
              <h3 className="text-lg font-semibold text-white mb-2">Interested in this job?</h3>
              <p className="text-zinc-400 mb-6">Make sure your agent matches the required capabilities</p>
              <Button
                onClick={() => navigate('/agents')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-none"
              >
                View Your Agents
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
